import {action, computed, observable} from "mobx";
import {bind, FormStore} from "Mri.Web.Common";
import {AccountStore} from "../../../Account/AccountStore";
import {AppStore} from "../../../Core/AppStore";
import {DisplayListViewModel} from "../../../DisplayFilters/DisplayListViewModel";
import {CellValueChangedEvent, SortChangedEvent} from "ag-grid-enterprise";
import {ISortField, sortFields} from "../GridFields/DisplayFields";
import {debounce} from "lodash";
import {ActiveDisplay} from "../../ActiveDisplay";
import {PinnedColumnManager} from "./PinnedColumnManager";
import {ColumnStateManager} from "./ColumnStateManager";
import {DisplaysStore} from "../../DisplaysStore";
import {GridConfig} from "../GridConfig";
import {GridScrollManager} from "./GridScrollManager";

export class GridEventManager {
    constructor(
        private accountStore: AccountStore,
        private appStore: AppStore,
        private displaysStore: DisplaysStore,
        private columnStateManager: ColumnStateManager,
        private pinnedColumnManager: PinnedColumnManager,
        private gridScrollManager: GridScrollManager,
        private vm: DisplayListViewModel,
    ) {}
    
    @bind
    @action
    public onGridChange(){
        this.debounceGridChange();
    };
    
    @bind
    @action
    public onToolPanelToggled() {
        this.pinnedColumnManager.applyMaxPinnedColumnContainer();
        this.pinnedColumnManager.positionResizers();
    }

    @bind
    @action
    public onSortChange(e: SortChangedEvent) {
        this.gridScrollManager.ignoreScrollCapture = true;
        this.sortChanging = true;

        // Scroll to top on sort change.
        this.appStore.gridRef?.api.ensureIndexVisible(0);

        let sort = e.columns?.map(c => {
            let colDef = this.appStore.gridRef?.api.getColumnDef(c.getColId());

            if (c.getSort()) {
                let sortField = sortFields.find((f: ISortField) => f.colId == c.getColId())?.sortString ?? c.getColId();
                if (colDef) {
                    colDef.cellClass = colDef.cellClass + " cell-sorting";
                }
                return sortField + ' ' + c.getSort();
            } else {
                if (colDef) {
                    colDef.cellClass = colDef.cellClass + "";
                }
            }
            return '';
        }) || [];

        sort = sort.filter(s => s != '');

        this.vm.sort = sort;

        this.handleGridChange();
    }

    @bind
    @action
    public async onCellChangedAsync(e: CellValueChangedEvent<ActiveDisplay>) {
        const display = e.data;
        const displayConnectionData = display?.displayConnectionData ?? {
            active: false,
            automaticallyManaged: false,
            cameraIP: null,
            comments: null,
            customerDisplayID: null,
            customerDisplayID2: null,
            displayBoardIP: '',
            displayBoardPort: 7100,
            displayBoardWebsitePort: 443,
            input1IP: null,
            input1Password: null,
            input2IP: null,
            input2Password: null,
            modemIP: null,
            nocServer: null,
            sslPort: 22,
        };

        const unitLocation = {
            address1: display?.unitLocation?.address1,
            address2: display?.unitLocation?.address2,
            city: display?.unitLocation?.city,
            country: display?.unitLocation?.country,
            customerSiteID: display?.unitLocation?.customerSiteID,
            customerSiteID2: display?.unitLocation?.customerSiteID2,
            description: display?.unitLocation?.description,
            postalCode: display?.unitLocation?.postalCode,
            stateOrProvince: display?.unitLocation?.stateOrProvince,
            latitude: display?.unitLocation?.latitude,
            longitude: display?.unitLocation?.longitude,
        };

        let displayForm = new FormStore<Mri.Web.Internal.Controllers.Displays.UpdateDisplayModel>({
            serialNumber: display?.serialNumber,
            contract: display?.contract,
            displayConnectionData,
            unitLocation,
        } as any);

        if (e.column.getColId() == "comments") {
            try {
                await this.displaysStore.updateDisplayCommentsAsync(
                    display.serialNumber,
                    {
                        displaySerialNumber: display.serialNumber,
                        comments: e.newValue
                    }
                );

            } catch (e: any) {
                console.log(e);
            }
        } else {
            try {
                await this.displaysStore.updateAsync(display.serialNumber, displayForm.values);

            } catch (e: any) {
                console.error(e);
            }
        }
    }

    @action
    public debounceGridChange = debounce(() => {
        this.handleGridChange();
    }, 1000);

    @bind
    @action
    private handleGridChange(){
        if (this.gridConfig.isImageColumnSelected) {
            this.rowHeight = this.accountStore.storage.gridPopVuRowHeight || 150;
        } else {
            this.rowHeight = this.accountStore.storage.gridRowHeight || 30;
        }

        this.columnStateManager.updateSearchableGridColumns();
        this.columnStateManager.updateActiveTabColumns();
        this.pinnedColumnManager.applyMaxPinnedColumnContainer();
        this.columnStateManager.overwriteActivePreset();
        this.gridScrollManager.restoreScrollPositions(0);
        this.gridScrollManager.restoreScrollPositions(500, true);
    }

    @observable
    public rowHeight?: number | undefined;
    @observable
    public sortChanging: boolean = false;
    @observable
    private gridConfig = new GridConfig(this.accountStore, this.appStore);
}
